
import React, { createContext, useState, useContext, ReactNode } from 'react';
import { ThemeProvider, createTheme, CssBaseline, GlobalStyles } from '@mui/material';
import { paletteDark, paletteLight } from '../../theme/palette';
import { typography } from '../../theme/typography';

interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/* ────────────────────────────────────────────────────────────────────────
 * Premium conference component overrides (visual only).
 * Palette drives semantic colours (primary = #1E4D8F blue → icons/labels/
 * focus, error = #B3212B red). These overrides add the premium shape:
 * contained-primary CTAs in conference red, secondary in blue, gold outlines,
 * floating cards, red switches, blue table headers, soft focus rings.
 * ──────────────────────────────────────────────────────────────────────── */
const BLUE = '#1E4D8F';
const BLUE_DARK = '#173E74';
const RED = '#B3212B';
const RED_DARK = '#991B23';
const GOLD = '#D4AF37';
const BORDER = '#E7E1D4';

const components = {
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ ownerState }: any) => ({
        borderRadius: 10,
        textTransform: 'none' as const,
        fontWeight: 600,
        transition: 'all 250ms ease',
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'primary' && {
            backgroundColor: RED,
            color: '#fff',
            '&:hover': { backgroundColor: RED_DARK, transform: 'translateY(-1px)' },
          }),
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'secondary' && {
            backgroundColor: BLUE,
            color: '#fff',
            '&:hover': { backgroundColor: BLUE_DARK, transform: 'translateY(-1px)' },
          }),
        ...(ownerState.variant === 'outlined' && {
          borderColor: GOLD,
          color: BLUE,
          '&:hover': { borderColor: GOLD, backgroundColor: 'rgba(212,175,55,0.08)' },
        }),
        '&:hover': { transform: 'translateY(-1px)' },
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        border: `1px solid ${BORDER}`,
        boxShadow: '0 10px 30px rgba(0,0,0,.05)',
        transition: 'box-shadow 250ms ease, transform 250ms ease',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 16 },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': { color: RED },
        '&.Mui-checked + .MuiSwitch-track': { backgroundColor: RED, opacity: 0.6 },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(30,77,143,.15)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BLUE },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: { backgroundColor: BLUE, color: '#fff', fontWeight: 700 },
    },
  },
};

const themeLight = createTheme({
  palette: paletteLight,
  typography,
  shape: { borderRadius: 10 },
  components,
});

const themeDark = createTheme({
  palette: paletteDark,
  typography,
  shape: { borderRadius: 10 },
  components,
});

export const ThemeProviderWrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  // Select the appropriate theme based on the current mode
  const theme = mode === 'light' ? themeLight : themeDark;

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />   
        <GlobalStyles
          styles={{
            "*": {
              scrollbarWidth: "thin", // Firefox
              // Color: `${theme.palette.primary.main} ${theme.palette.background.paper}`, //  thumb and track color for Firefox
            },
            "*::-webkit-": {
              width: "1px", // Width of the  for Webkit browsers
            },
            "*::-webkit--track": {
              background: theme.palette.background.paper, // Track background color
            },
            "*::-webkit--thumb": {
              backgroundColor: theme.palette.primary.main, // Thumb color
              borderRadius: "10px", // Rounded edges for the thumb
            },
          }}
        />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProviderWrapper');
  }
  return context;
};
